import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Tooltip,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import {
  Delete as DeleteIcon,
  HelpOutline as HelpOutlineIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from '@mui/icons-material'
import { QuestionType } from '../types/questionReport'
import { QuestionReportService } from '../services/questionReportService'
import { QuestionReport } from '../types/questionReport'

export const QuestionReportPage: React.FC = () => {
  const { t } = useTranslation('questionReport')

  const [reports, setReports] = useState<QuestionReport[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [total, setTotal] = useState(0)

  // 削除ダイアログ
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedReport, setSelectedReport] = useState<QuestionReport | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  // 詳細表示ダイアログ
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  const [detailReport, setDetailReport] = useState<QuestionReport | null>(null)

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  const fetchReports = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await QuestionReportService.getReports({
        page: page + 1,
        limit: rowsPerPage,
      })
      setReports(response.reports)
      setTotal(response.pagination.total)
    } catch {
      setError(t('errors.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, t])

  useEffect(() => {
    fetchReports()
  }, [fetchReports])

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleOpenDeleteDialog = (report: QuestionReport) => {
    setSelectedReport(report)
    setDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setSelectedReport(null)
  }

  const handleOpenDetailDialog = (report: QuestionReport) => {
    setDetailReport(report)
    setDetailDialogOpen(true)
  }

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false)
    setDetailReport(null)
  }

  const getQuestionTypeLabel = (type: QuestionType): string => {
    return t(`detail.questionTypes.${type}`)
  }

  const isCorrectOption = (
    option: string,
    correctAnswer: string,
    questionType: QuestionType
  ): boolean => {
    if (questionType === 'MULTIPLE_CHOICE') {
      const correctAnswers = correctAnswer.split(',').map((a) => a.trim())
      return correctAnswers.includes(option)
    }
    return option === correctAnswer
  }

  const handleDelete = async () => {
    if (!selectedReport) return

    setDeleteLoading(true)
    try {
      await QuestionReportService.deleteReport(selectedReport.id)
      setSnackbar({
        open: true,
        message: t('delete.success'),
        severity: 'success',
      })
      handleCloseDeleteDialog()
      fetchReports()
    } catch {
      setSnackbar({
        open: true,
        message: t('delete.error'),
        severity: 'error',
      })
    } finally {
      setDeleteLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <HelpOutlineIcon sx={{ mr: 2, fontSize: 32, color: 'text.secondary' }} />
        <Box>
          <Typography variant="h4" component="h1">
            {t('title')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('pageDescription')}
          </Typography>
        </Box>
      </Box>

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* テーブル */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('list.columns.question')}</TableCell>
                <TableCell>{t('list.columns.test')}</TableCell>
                <TableCell>{t('list.columns.reporter')}</TableCell>
                <TableCell>{t('list.columns.comment')}</TableCell>
                <TableCell>{t('list.columns.createdAt')}</TableCell>
                <TableCell align="right">{t('list.columns.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">{t('list.noReports')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report) => (
                  <TableRow
                    key={report.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleOpenDetailDialog(report)}
                  >
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Tooltip title={report.question?.questionText || ''}>
                        <Typography variant="body2" noWrap>
                          {truncateText(report.question?.questionText || '', 50)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{report.test?.title || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{report.reporter?.name || '-'}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {report.reporter?.email || ''}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Tooltip title={report.comment || ''}>
                        <Typography
                          variant="body2"
                          color={report.comment ? 'textPrimary' : 'textSecondary'}
                        >
                          {report.comment ? truncateText(report.comment, 30) : t('list.noComment')}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(report.createdAt)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('detail.title')}>
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label={t('detail.title')}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleOpenDetailDialog(report)
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('delete.title')}>
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={t('delete.title')}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleOpenDeleteDialog(report)
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="表示件数:"
        />
      </Paper>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>{t('delete.title')}</DialogTitle>
        <DialogContent>
          <Typography>{t('delete.confirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
            キャンセル
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleteLoading}>
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 詳細表示ダイアログ */}
      <Dialog open={detailDialogOpen} onClose={handleCloseDetailDialog} maxWidth="md" fullWidth>
        <DialogTitle>{t('detail.title')}</DialogTitle>
        <DialogContent dividers>
          {detailReport?.question && (
            <Box>
              {/* 設問情報 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {t('detail.questionText')}
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {detailReport.question.questionText}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Chip
                  label={`${t('detail.questionType')}: ${getQuestionTypeLabel(detailReport.question.questionType)}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={`${t('detail.points')}: ${detailReport.question.points}`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              </Box>

              {/* 選択肢（選択式の場合） */}
              {detailReport.question.options && detailReport.question.options.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {t('detail.options')}
                  </Typography>
                  <List dense>
                    {detailReport.question.options.map((option, index) => {
                      const isCorrect = isCorrectOption(
                        option,
                        detailReport.question!.correctAnswer,
                        detailReport.question!.questionType
                      )
                      return (
                        <ListItem key={index}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {isCorrect ? (
                              <CheckCircleIcon color="success" />
                            ) : (
                              <RadioButtonUncheckedIcon color="disabled" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={option}
                            primaryTypographyProps={{
                              color: isCorrect ? 'success.main' : 'text.primary',
                              fontWeight: isCorrect ? 'bold' : 'normal',
                            }}
                          />
                        </ListItem>
                      )
                    })}
                  </List>
                </Box>
              )}

              {/* 正解（記述式の場合） */}
              {detailReport.question.questionType === 'SHORT_ANSWER' && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {t('detail.correctAnswer')}
                  </Typography>
                  <Typography variant="body1" color="success.main" fontWeight="bold">
                    {detailReport.question.correctAnswer}
                  </Typography>
                </Box>
              )}

              {/* 解説 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {t('detail.explanation')}
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {detailReport.question.explanation || t('detail.noExplanation')}
                  </Typography>
                </Paper>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* 報告情報 */}
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                {t('detail.reportInfo')}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t('detail.reporter')}
                  </Typography>
                  <Typography variant="body2">
                    {detailReport.reporter?.name || '-'}
                    {detailReport.reporter?.email && (
                      <Typography component="span" color="textSecondary" sx={{ ml: 1 }}>
                        ({detailReport.reporter.email})
                      </Typography>
                    )}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t('detail.reportedAt')}
                  </Typography>
                  <Typography variant="body2">{formatDate(detailReport.createdAt)}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t('detail.comment')}
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'warning.50' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {detailReport.comment || t('detail.noComment')}
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailDialog} variant="contained">
            {t('detail.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default QuestionReportPage
